import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import JSZip from "jszip";
import api from "../../../../services/api";
import VariantButton from "../../../../components/variant-button/variant-button";

const LOCALES = [
  { code: "vi", label: "Tiếng Việt" },
  { code: "en", label: "English" },
  { code: "ja", label: "日本語" },
  { code: "zh-Hans", label: "简体中文" },
  { code: "zh-Hant", label: "繁體中文" },
];
const IMAGE_MIME = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
};
const INPUT_CLASS =
  "w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500";

const emptyForms = () => Object.fromEntries(
  LOCALES.map(({ code }) => [code, { title: "", slug: "", blog_type_id: "" }]),
);

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[char]);
}

function normalizeImagePath(rawImage, locale, index) {
  if (typeof rawImage !== "string" || !rawImage.trim()) {
    throw new Error(locale + "-data.json: mục " + (index + 1) + " thiếu image.");
  }
  const image = rawImage.trim().replace(/\\/g, "/").replace(/^(\.\/)+/g, "");
  const parts = image.split("/");
  if (image.startsWith("/") || parts.some((part) => !part || part === ".." || part === ".") ||
      /^[a-z]+:/i.test(image)) {
    throw new Error("Đường dẫn ảnh không hợp lệ: " + rawImage);
  }
  const ext = image.split(".").pop().toLowerCase();
  if (!IMAGE_MIME[ext]) {
    throw new Error("Ảnh " + image + " không phải JPG, PNG, WEBP hoặc GIF.");
  }
  return {
    relativePath: image.startsWith("images/") ? image : "images/" + image,
    mime: IMAGE_MIME[ext],
  };
}

// The ZIP may be rooted at / or have one parent directory.
// All five manifests reference the same images/ directory.
async function readBlogZip(file) {
  if (!file || !/\.zip$/i.test(file.name)) {
    throw new Error("Vui lòng chọn file .zip.");
  }
  const zip = await JSZip.loadAsync(file);
  const found = new Map();
  for (const entry of Object.values(zip.files)) {
    if (entry.dir || entry.name.startsWith("__MACOSX/")) continue;
    const name = entry.name.split("/").pop();
    const locale = LOCALES.find(({ code }) => name === code + "-data.json")?.code;
    if (!locale) continue;
    if (found.has(locale)) {
      throw new Error("ZIP chứa nhiều file " + locale + "-data.json.");
    }
    found.set(locale, entry);
  }
  const missing = LOCALES.filter(({ code }) => !found.has(code));
  if (missing.length) {
    throw new Error("ZIP thiếu: " + missing.map(({ code }) => code + "-data.json").join(", "));
  }

  const directory = found.get("vi").name.replace(/[^/]+$/, "");
  if ([...found.values()].some((entry) => entry.name.replace(/[^/]+$/, "") !== directory)) {
    throw new Error("Cả 5 file *-data.json phải nằm trong cùng một thư mục.");
  }

  const byLocale = {};
  const images = new Map();
  for (const { code } of LOCALES) {
    const manifest = found.get(code);
    let items;
    try {
      items = JSON.parse((await manifest.async("string")).replace(/^\uFEFF/, ""));
    } catch {
      throw new Error(code + "-data.json không phải JSON hợp lệ.");
    }
    if (!Array.isArray(items) || items.length === 0) {
      throw new Error(code + '-data.json phải là mảng [{"name":"...", "image":"..."}].');
    }
    byLocale[code] = items.map((item, index) => {
      if (!item || typeof item.name !== "string" || !item.name.trim()) {
        throw new Error(code + "-data.json: mục " + (index + 1) + " thiếu name.");
      }
      const { relativePath, mime } = normalizeImagePath(item.image, code, index);
      const imagePath = directory + relativePath;
      const imageEntry = zip.file(imagePath);
      if (!imageEntry || imageEntry.dir) {
        throw new Error("Không tìm thấy ảnh " + imagePath + " (" + code + ").");
      }
      if (!images.has(imagePath)) {
        images.set(imagePath, {
          imagePath,
          imageEntry,
          filename: relativePath.split("/").pop(),
          mime,
        });
      }
      return { name: item.name.trim(), imagePath };
    });
  }

  // Prevent silently creating translations with a missing/different image set.
  const expected = [...new Set(byLocale.vi.map((item) => item.imagePath))].sort().join("\n");
  for (const { code } of LOCALES) {
    const actual = [...new Set(byLocale[code].map((item) => item.imagePath))].sort().join("\n");
    if (actual !== expected || byLocale[code].length !== byLocale.vi.length) {
      throw new Error(code + "-data.json phải dùng cùng bộ ảnh và số mẫu như vi-data.json.");
    }
  }

  return { byLocale, images: [...images.values()] };
}

function makeHtml(entries, imageUrls) {
  return entries.map(({ name, imagePath }) => {
    const safeName = escapeHtml(name);
    const safeUrl = escapeHtml(imageUrls.get(imagePath));
    return "<h2>" + safeName + "</h2>\n<figure><img src=\"" + safeUrl +
      "\" alt=\"" + safeName + "\" loading=\"lazy\" /><figcaption>" +
      safeName + "</figcaption></figure>";
  }).join("\n");
}

export default function AddBlogFromZip() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const uploadedUrlsRef = useRef(new Map());
  const [permissions, setPermissions] = useState(null);
  const [blogTypes, setBlogTypes] = useState([]);
  const [forms, setForms] = useState(emptyForms);
  const [selectedFile, setSelectedFile] = useState(null);
  const [packageData, setPackageData] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const [permissionsResult, typesResult] = await Promise.all([
          api.get("/users/permissions"),
          api.get("/blogTypes"),
        ]);
        setPermissions(permissionsResult.data.data || []);
        setBlogTypes(typesResult.data.data || []);
      } catch {
        setError("Không thể tải quyền hoặc danh sách loại blog.");
        setPermissions([]);
      }
    }
    load();
  }, []);

  function setField(locale, name, value) {
    setForms((previous) => ({
      ...previous,
      [locale]: { ...previous[locale], [name]: value },
    }));
  }

  async function selectZip(file) {
    if (!file || submitting) return;
    setError("");
    setStatus("Đang đọc ZIP...");
    setSelectedFile(null);
    setPackageData(null);
    uploadedUrlsRef.current = new Map();
    setUploadedCount(0);
    try {
      const parsed = await readBlogZip(file);
      setPackageData(parsed);
      setSelectedFile(file);
    } catch (err) {
      setError(err.message || "Không thể đọc ZIP.");
    } finally {
      setStatus("");
    }
  }

  async function uploadImages(entries) {
    const urls = uploadedUrlsRef.current;
    const pending = entries.filter((entry) => !urls.has(entry.imagePath));
    if (!pending.length) return urls;

    const pocketBase = (process.env.REACT_APP_POCKETBASE || "").replace(/\/+$/, "");
    if (!pocketBase) throw new Error("Chưa cấu hình REACT_APP_POCKETBASE.");

    setStatus("Đang kết nối PocketBase...");
    const authResponse = await axios.post(
      pocketBase + "/api/collections/_superusers/auth-with-password",
      {
        identity: process.env.REACT_APP_IDENTITY,
        password: process.env.REACT_APP_PASSWORD,
      },
    );
    const token = authResponse.data.token;
    if (!token) throw new Error("PocketBase không trả về token.");

    setStatus("Đang upload ảnh dùng chung...");
    for (let start = 0; start < pending.length; start += 3) {
      await Promise.all(pending.slice(start, start + 3).map(async (entry) => {
        const blob = await entry.imageEntry.async("blob");
        if (blob.size > 5 * 1024 * 1024) {
          throw new Error("Ảnh " + entry.filename + " vượt quá 5MB.");
        }
        const body = new FormData();
        body.append("image", new File([blob], entry.filename, { type: entry.mime }));
        const response = await axios.post(
          pocketBase + "/api/collections/blog_images/records",
          body,
          { headers: { Authorization: token } },
        );
        const { id, collectionName, image } = response.data;
        if (!id || !collectionName || !image) {
          throw new Error("PocketBase không trả URL cho " + entry.filename);
        }
        const imageUrl = pocketBase + "/api/files/" + collectionName +
          "/" + id + "/" + encodeURIComponent(image);
        await api.post("/blogs/images", { image_url: imageUrl });
        urls.set(entry.imagePath, imageUrl);
        setUploadedCount(urls.size);
      }));
    }
    return urls;
  }

  const complete = LOCALES.every(({ code }) => {
    const form = forms[code];
    return form.title.trim() && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(form.slug.trim()) &&
      form.blog_type_id && blogTypes.some((type) =>
        type.id === form.blog_type_id && type.locale === code);
  });
  const slugs = LOCALES.map(({ code }) => forms[code].slug.trim());
  const uniqueSlugs = new Set(slugs).size === LOCALES.length;
  const canSubmit = Boolean(packageData && complete && uniqueSlugs && !submitting);

  async function handleSubmit() {
    if (submitting) return;
    if (!canSubmit) {
      setError("Cần ZIP đủ 5 ngôn ngữ và tiêu đề, slug duy nhất, loại blog đúng locale cho từng ngôn ngữ.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const metadata = LOCALES.map(({ code }) => ({
        locale: code,
        title: forms[code].title.trim(),
        slug: forms[code].slug.trim(),
        blog_type_id: forms[code].blog_type_id,
      }));
      // Check all slugs and localized blog types BEFORE uploading images.
      setStatus("Đang kiểm tra thông tin cả 5 ngôn ngữ...");
      await api.post("/blogs/bulk", { dryRun: true, blogs: metadata });

      const imageUrls = await uploadImages(packageData.images);
      const blogs = metadata.map((item) => ({
        ...item,
        image_url: imageUrls.get(packageData.byLocale[item.locale][0].imagePath),
        content: makeHtml(packageData.byLocale[item.locale], imageUrls),
      }));
      setStatus("Đang lưu 5 bài blog...");
      await api.post("/blogs/bulk", { blogs });
      navigate("/blogs");
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Không thể tạo blog.");
      setStatus("");
    } finally {
      setSubmitting(false);
    }
  }

  if (permissions && !permissions.includes("blogs.create")) return null;
  if (!permissions) return null;

  return (
    <div className="flex-1 overflow-y-auto p-6 bg-gray-100 min-h-screen">
      <div className="w-full max-w-5xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Quản lý Blog</h1>
        <div className="bg-white shadow-xl rounded-lg p-6 space-y-6">
          <div>
            <h2 className="text-2xl font-semibold text-gray-700 mb-2">Thêm blog đa ngôn ngữ từ ZIP</h2>
            <p className="text-sm text-gray-600">
              Một ZIP tạo 5 bài (vi, en, ja, zh-Hans, zh-Hant). Ảnh chỉ upload một lần và dùng chung cho cả 5 bài.
              Tác giả được chọn ngẫu nhiên theo ngôn ngữ từ danh sách seed.
            </p>
          </div>

          {error && <p role="alert" className="bg-red-100 text-red-700 border border-red-300 p-3 rounded-md">{error}</p>}

          <section>
            <h3 className="font-semibold text-gray-800 mb-2">1. Chọn ZIP chứa 5 file dữ liệu và images/</h3>
            <p className="text-sm text-gray-600 mb-3">
              Cần vi-data.json, en-data.json, ja-data.json, zh-Hans-data.json, zh-Hant-data.json
              và images/ nằm cùng cấp (hoặc bên trong một thư mục cha).
              Mỗi file JSON là mảng các mục có name và image. Các ngôn ngữ dùng cùng bộ ảnh.
            </p>
            <input
              ref={inputRef}
              type="file"
              accept=".zip,application/zip,application/x-zip-compressed"
              className="hidden"
              disabled={submitting}
              onChange={(event) => {
                selectZip(event.target.files?.[0]);
                event.target.value = "";
              }}
            />
            <div
              role="button"
              tabIndex={submitting ? -1 : 0}
              onClick={() => !submitting && inputRef.current?.click()}
              onKeyDown={(event) => {
                if (!submitting && (event.key === "Enter" || event.key === " ")) {
                  event.preventDefault();
                  inputRef.current?.click();
                }
              }}
              onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
              onDragLeave={(event) => { event.preventDefault(); setDragging(false); }}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                if (!submitting) selectZip(event.dataTransfer.files?.[0]);
              }}
              className={"border-2 border-dashed rounded-lg px-6 py-8 text-center transition-colors " +
                (dragging ? "border-[#34699A] bg-blue-50 " : "border-gray-300 bg-gray-50 ") +
                (submitting ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:border-[#34699A]")}
            >
              <p className="font-semibold text-gray-700">
                {selectedFile ? selectedFile.name : "Kéo thả ZIP hoặc bấm để chọn"}
              </p>
            </div>

            {packageData && (
              <div className="mt-3 p-4 border border-gray-200 rounded-lg">
                <p className="font-semibold text-gray-800">
                  Hợp lệ: {packageData.images.length} ảnh dùng chung cho 5 ngôn ngữ
                </p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3 text-sm text-gray-700">
                  {LOCALES.map(({ code, label }) => (
                    <div key={code} className="bg-gray-50 p-2 rounded">
                      <strong>{label}</strong> ({code})
                      <p>{packageData.byLocale[code].length} mẫu</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          <section>
            <h3 className="font-semibold text-gray-800 mb-3">2. Nhập tiêu đề, slug và loại blog cho từng ngôn ngữ</h3>
            <div className="space-y-4">
              {LOCALES.map(({ code, label }) => {
                const types = blogTypes.filter((type) => type.locale === code);
                return (
                  <div key={code} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-3">{label} ({code})</h4>
                    <div className="grid gap-3 md:grid-cols-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Tiêu đề *
                        <input
                          type="text"
                          value={forms[code].title}
                          onChange={(event) => setField(code, "title", event.target.value)}
                          placeholder="Tiêu đề bài viết"
                          className={INPUT_CLASS + " mt-1"}
                          disabled={submitting}
                        />
                      </label>
                      <label className="block text-sm font-medium text-gray-700">
                        Slug *
                        <input
                          type="text"
                          value={forms[code].slug}
                          onChange={(event) => setField(code, "slug", event.target.value)}
                          placeholder="slug-rieng-cho-locale"
                          className={INPUT_CLASS + " mt-1"}
                          disabled={submitting}
                        />
                      </label>
                      <label className="block text-sm font-medium text-gray-700 md:col-span-2">
                        Phân loại *
                        <select
                          value={forms[code].blog_type_id}
                          onChange={(event) => setField(code, "blog_type_id", event.target.value)}
                          className={INPUT_CLASS + " mt-1"}
                          disabled={submitting}
                        >
                          <option value="">Chọn loại blog ({code})</option>
                          {types.map((type) => (
                            <option value={type.id} key={type.id}>{type.name}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                    {packageData && (
                      <p className="mt-2 text-xs text-gray-500">
                        Nội dung lấy từ {code}-data.json; ảnh bìa lấy từ mẫu đầu tiên. Tác giả chọn tự động.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {status && (
            <p role="status" className="text-sm text-blue-700">
              {status}{submitting && packageData && packageData.images.length
                ? " (" + uploadedCount + "/" + packageData.images.length + " ảnh)" : ""}
            </p>
          )}
          {!canSubmit && (
            <p className="text-sm text-amber-700">
              Chỉ được tạo khi ZIP hợp lệ và thông tin của cả 5 ngôn ngữ đã điền đầy đủ.
              Slug phải duy nhất và dùng chữ thường, số hoặc dấu gạch ngang.
            </p>
          )}

          <div className="flex space-x-3 justify-end">
            <div className="w-[160px]">
              <VariantButton props={{
                onClick: handleSubmit,
                title: submitting ? "Đang tạo..." : "Tạo 5 bài blog",
                disabled: !canSubmit,
              }} />
            </div>
            <div className="w-[120px]">
              <Link to="/blogs">
                <VariantButton props={{ title: "Trở lại", onClick: null }} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
